import { TZDateMini } from '@date-fns/tz';
import { endOfDay, startOfDay } from 'date-fns';
import type { DebouncedFunc } from 'lodash-es';
import type { Dispatch, RefObject } from 'react';
import type { IntlShape } from 'react-intl';
import type { StoreApi } from 'zustand';

import { getTimezone } from '@dynatrace-sdk/user-preferences';

import type { FormFieldMessagesReducerAction } from '../../../../forms/forms-core/state/formfield-messages-state-reducer.js';
import { sortTimeframeValues } from '../../../../forms/forms-core/time/helpers.js';
import {
  removePrecision,
  removePrecisionFromTimeFrame,
} from '../../../../forms/forms-core/time/timeParser.js';
import type { TimeRangePickerRef } from '../../../../forms/shared-types.js';
import { type Timeframe } from '../../../types/time.js';
import {
  isFullTimeframe,
  isNowTimeValue,
  isTimeValue,
  mergeRangeValue,
} from '../../helpers.js';
import { isOutOfBounds, validate } from '../../validation.js';
import type { ValidateOptions } from '../../validation.js';
import type {
  TimeframeSelectorState,
  TimeframeSelectorStoreActions,
} from '../timeframeSelector.types.js';
import { getTimeframeFromCalendarSelection } from './calendar.js';
import {
  applySetToNow,
  closeCalendarAndFocusInput,
  handleClearCalendarValue,
} from './helpers.js';

/**
 * Handles setting the TimeframeSelector timeframe value.
 * Validates the input, calls onChange callback, closes the selector,
 * and updates all related state (selector, calendar, dateTimePicker).
 * @internal
 */
function handleTimeframeSelectorTimeframeChange(
  inputValue: Timeframe | null,
  state: TimeframeSelectorState,
  isControlled: boolean,
  timeframeSelectorRef: RefObject<TimeRangePickerRef | null>,
  formFieldMessagesDispatch?: Dispatch<FormFieldMessagesReducerAction>,
  onChangeProp?: (value: Timeframe | null) => void,
  errorMessages?: ValidateOptions['errorMessages'],
  intl?: IntlShape,
): Partial<TimeframeSelectorState> {
  const timeframeWithPrecision =
    removePrecisionFromTimeFrame(inputValue, state.precision) ?? null;

  const value = isFullTimeframe(timeframeWithPrecision)
    ? timeframeWithPrecision
    : null;

  onChangeProp?.(value);
  timeframeSelectorRef.current?.close();

  if (isControlled) {
    return state;
  }

  const error = validate(timeframeWithPrecision, state.min, state.max, {
    formMessageDispatch: formFieldMessagesDispatch,
    intl,
    formerError: state.timeframeSelectorError,
    precision: state.precision,
    showError: false,
    checkOrder: true,
    ref: timeframeSelectorRef,
    expectedFormat: state.formatString,
    valueMode: 'timeframeSelector',
    required: state.required,
    errorMessages,
  });

  return {
    timeframeSelectorTimeframe: value,
    calendarValue: value ?? timeframeWithPrecision,
    dateTimeRangePickerTimeframe: value ?? timeframeWithPrecision,
    referenceTimeframe: null,
    timeframeSelectorError: error,
    calendarError: error,
    dateTimeRangePickerError: error,
  };
}

/**
 * Handles setting the previous timeframe value.
 * Used for tracking the last valid timeframe before changes.
 * @internal
 */
function handlePreviousTimeframeChange(
  value: Timeframe | null,
): Partial<TimeframeSelectorState> {
  return {
    referenceTimeframe: value,
  };
}

function isFullDayTimeframe(
  timeframe: Timeframe,
  state: TimeframeSelectorState,
): boolean {
  const timeZone = getTimezone();
  const to = new TZDateMini(timeframe.to.absoluteDate, timeZone);
  const from = new TZDateMini(timeframe.from.absoluteDate, timeZone);

  const isToAtEndOfDay =
    removePrecision(endOfDay(to).toISOString(), state.precision) ===
    removePrecision(timeframe.to.absoluteDate, state.precision);
  const isToAtMax = state.max?.absoluteDate === to.toISOString();

  const isFromAtStartOfDay =
    removePrecision(startOfDay(from).toISOString(), state.precision) ===
    removePrecision(timeframe.from.absoluteDate, state.precision);
  const isFromAtMin = state.min?.absoluteDate === from.toISOString();

  return Boolean(
    (isToAtEndOfDay || isToAtMax) && (isFromAtStartOfDay || isFromAtMin),
  );
}

/**
 * Creates actions for the TimeframeSelector store using the slice action handlers.
 * @internal
 */
export function getTimeframeSelectorActions(
  isControlled: boolean,
  timeframeSelectorRef: RefObject<TimeRangePickerRef | null>,
  formFieldMessagesDispatch?: Dispatch<FormFieldMessagesReducerAction>,
  onChangeProp?: ((value: Timeframe | null) => void) | undefined,
  debouncedOnChangeRef?: RefObject<
    DebouncedFunc<(timeframe: Timeframe | null) => void>
  >,
  errorMessages?: ValidateOptions['errorMessages'],
  intl?: IntlShape,
) {
  const actions = (
    set: StoreApi<
      TimeframeSelectorState & TimeframeSelectorStoreActions
    >['setState'],
  ): TimeframeSelectorStoreActions => {
    return {
      setCalendarValue: (inputValue) => {
        set((state) => {
          // TimeValue input (e.g. a "now" expression): apply it as the "to" boundary directly.
          if (isTimeValue(inputValue) && inputValue.value === 'now()') {
            const update = applySetToNow(state, {
              validateMode: 'timeframeSelector',
              errorMessages,
            });
            // nowInserted: true only in the "pending" case — button clicked with no existing
            // from, so calendar stays open waiting for the complementary boundary.
            const nowInserted =
              'dateTimeRangePickerTimeframe' in update &&
              !update.dateTimeRangePickerTimeframe?.from &&
              isNowTimeValue(update.dateTimeRangePickerTimeframe?.to);

            return {
              timeframeSelectorError: undefined,
              isFullDay: false,
              ...update,
              nowInserted,
            };
          }

          const calendarValue = getTimeframeFromCalendarSelection(
            inputValue,
            state,
          );

          const nowIsOutOfBounds =
            isOutOfBounds(
              'now',
              isNowTimeValue(state.min) ? null : state.min,
              isNowTimeValue(state.max) ? null : state.max,
            ) !== undefined;

          // The user set `to = now` (via button or typing) without a `from` yet.
          // When they click a calendar date, pair it with the existing `to = now`.
          if (
            !nowIsOutOfBounds &&
            !state.dateTimeRangePickerTimeframe?.from &&
            state.dateTimeRangePickerTimeframe?.to &&
            isNowTimeValue(state.dateTimeRangePickerTimeframe?.to) &&
            calendarValue?.from &&
            !calendarValue?.to
          ) {
            const timeframe = sortTimeframeValues(
              {
                from: calendarValue.from,
                to: state.dateTimeRangePickerTimeframe.to,
              },
              state.min,
              state.max,
              state.precision,
            );
            closeCalendarAndFocusInput(
              state.dateTimeRangePickerRef,
              state.dateTimeFromRef,
            );

            return {
              calendarValue: timeframe,
              dateTimeRangePickerTimeframe: timeframe,
              calendarError: undefined,
              dateTimeRangePickerError: undefined,
              timeframeSelectorError: undefined,
              isFullDay: false,
              nowInserted: false,
            };
          }

          if (isFullTimeframe(calendarValue)) {
            const isFullDay = isFullDayTimeframe(calendarValue, state);
            closeCalendarAndFocusInput(
              state.dateTimeRangePickerRef,
              state.dateTimeFromRef,
            );

            return {
              calendarValue,
              dateTimeRangePickerTimeframe: calendarValue,
              isFullDay,
              nowInserted: false,
            };
          }

          return { calendarValue, isFullDay: false, nowInserted: false };
        });
      },
      setDateTimeRangePickerTimeframe: (value) => {
        return set((state) => {
          // Full clear: null resets both fields to null and errors to undefined
          if (value === null) {
            return {
              dateTimeRangePickerTimeframe: null,
              calendarValue: null,
              dateTimeRangePickerError: undefined,
              calendarError: undefined,
              nowInserted: false,
            };
          }

          const merged = mergeRangeValue(
            state.dateTimeRangePickerTimeframe,
            value,
          );

          const timeframeWithPrecision =
            removePrecisionFromTimeFrame(merged, state.precision) ?? null;

          const isPartialFrom =
            isTimeValue(merged.from) && merged.from.absoluteDate === '';
          const isPartialTo =
            isTimeValue(merged.to) && merged.to.absoluteDate === '';

          // For partial values while editing we trigger onChange with '' to inform
          // the user that the current value is invalid, but not empty.
          if (isPartialFrom || isPartialTo) {
            const errorKey = validate(
              timeframeWithPrecision,
              state.min,
              state.max,
              {
                formerError: state.dateTimeRangePickerError,
                precision: state.precision,
                showError: false,
                checkOrder: true,
                ref: state.dateTimeRangePickerRef,
                expectedFormat: state.formatString,
                valueMode: 'timeframeSelector',
                required: state.required,
                errorMessages,
              },
            );

            const calendarValue = {
              from: isPartialFrom
                ? null
                : (timeframeWithPrecision?.from ?? null),
              to: isPartialTo ? null : (timeframeWithPrecision?.to ?? null),
            };

            return {
              dateTimeRangePickerTimeframe: timeframeWithPrecision,
              calendarValue,
              calendarError: errorKey,
              dateTimeRangePickerError: errorKey,
              isFullDay: false,
            };
          }

          const isFullDay = isFullTimeframe(timeframeWithPrecision)
            ? isFullDayTimeframe(timeframeWithPrecision, state)
            : false;

          const dateTimeRangePickerError = validate(
            timeframeWithPrecision,
            state.min,
            state.max,
            {
              formerError: state.dateTimeRangePickerError,
              precision: state.precision,
              showError: false,
              checkOrder: true,
              ref: state.dateTimeRangePickerRef,
              expectedFormat: state.formatString,
              valueMode: 'timeframeSelector',
              required: state.required,
              errorMessages,
            },
          );

          return {
            dateTimeRangePickerTimeframe: timeframeWithPrecision,
            calendarValue: timeframeWithPrecision,
            calendarError: dateTimeRangePickerError,
            dateTimeRangePickerError,
            isFullDay,
          };
        });
      },
      setTimeframeSelectorTimeframe: (inputValue) => {
        debouncedOnChangeRef?.current?.cancel();
        return set((state) =>
          handleTimeframeSelectorTimeframeChange(
            inputValue,
            state,
            isControlled,
            timeframeSelectorRef,
            formFieldMessagesDispatch,
            onChangeProp,
            errorMessages,
            intl,
          ),
        );
      },
      setReferenceTimeframe: (value) => {
        set(() => handlePreviousTimeframeChange(value));
      },
      setStepperValue: (value) => {
        set((state) => {
          debouncedOnChangeRef?.current?.(value);
          if (isControlled) {
            return state;
          }
          return {
            timeframeSelectorTimeframe: value,
            calendarValue: value,
            dateTimeRangePickerTimeframe: value,
          };
        });
      },
      clearCalendarValue: () => {
        set((state) => handleClearCalendarValue(state));
      },
    };
  };

  return actions;
}
